import { useState, type CSSProperties } from 'react';
import { Drawer } from '@/components/Drawer';
import { Icon, type IconName } from '@/components/Icon';
import {
  PATCH_NOTES,
  PATCH_NOTE_KIND_LABEL,
  PATCH_NOTE_PART_LABEL,
  markPatchNotesSeen,
  patchNotesVersion,
  readSeenPatchNotes,
  type PatchNoteItem,
  type PatchNoteKind,
  type PatchNotePart,
} from '@/patch-notes';
import { useNewVersionAvailable } from '@/lib/new-version-check';

// 상단 바 [패치노트] — 2026-10-19 오너 요청. 새 판(patch-notes.ts 맨 위가 바뀜)을 아직 안 봤으면
// 버튼이 깜빡이고 점이 붙는다. 누르면 오른쪽 서랍에 일자별 내역, 그 순간 '봤음'으로 적는다.
//
// 새 판 배포 알림(2026-10-19 오너) — 열어 둔 화면은 옛 번들로 돌아 새 패치노트를 모른다. 그래서
// 몇 분마다 새 판이 나갔는지 보고(useNewVersionAvailable), 나갔으면 버튼이 깜빡이고 옆에
// [새로고침] 이 붙는다. 누르면 새 판을 받는다 — 그 뒤 새 패치노트로 다시 깜빡인다.

function reloadToLatest() {
  window.location.reload();
}

const KIND_STYLE: Record<PatchNoteKind, { color: string; bg: string }> = {
  new: { color: 'var(--fg-brand)', bg: 'var(--brand-tint)' },
  ui: { color: 'var(--robot-shipping)', bg: 'var(--robot-shipping-tint)' },
  fix: { color: 'var(--fg-on-warning-tint)', bg: 'var(--bg-warning-subtle)' },
};
// 2026-11-02 오너 — 파트 말머리(공통 · 사업 · 제조 · 필드)를 상태 배지('해결')와 같은 모양의 색 배지로.
// 색은 사이드바 묶음 색(section-accent.ts)과 같은 계열이다. 주황은 옅은 바탕에서 안 읽혀 진한 주황 글씨를 쓴다.
// 줄의 아이콘 상자도 파트 색을 따른다 — 종류 색까지 섞으면 한 줄에 뜻이 다른 같은 색이 둘 생긴다(종류는 묶음 제목이 알린다).
const PART_STYLE: Record<PatchNotePart, { color: string; bg: string }> = {
  common: { color: 'var(--fg-muted)', bg: 'var(--bg-sunken)' },
  business: { color: 'var(--robot-shipping)', bg: 'var(--robot-shipping-tint)' },
  manufacturing: { color: 'var(--fg-on-warning-tint)', bg: 'var(--robot-installing-tint)' },
  field: { color: 'var(--fg-brand)', bg: 'var(--brand-tint)' },
};
// 2026-10-27 오너 "패치노트에 저 앞 빈공간에도 적절한 아이콘으로 채워넣자" — 줄마다 내용에 맞는 아이콘.
// 지난 항목 90여 일치에 하나씩 달 수는 없으니 글에 나온 말로 고른다(앞에 있는 말이 이긴다).
// 맞지 않으면 그 항목에 icon 을 직접 적는다.
const ICON_BY_WORD: Array<[RegExp, IconName]> = [
  [/지도/, 'map-pin'],
  [/대시보드|그래프|차트/, 'grid'],
  [/패치노트/, 'scroll'],
  [/알림|깜빡/, 'bell'],
  [/검색/, 'search'],
  [/서명|전자계약/, 'signature'],
  [/계약/, 'file'],
  [/문의|전화|통화/, 'phone'],
  [/메일|이메일/, 'mail'],
  [/메시지|문자/, 'message'],
  [/배치|출고|배송|설치|회수/, 'truck'],
  [/부품|재고|자재|발주/, 'package'],
  [/수리|점검|정비|AS|A\/S/, 'wrench'],
  [/생산|제조|로더/, 'factory'],
  [/로봇/, 'bot'],
  [/고객사|매장/, 'store'],
  [/사용자|직원|권한|역할/, 'users'],
  [/일정|달력|날짜/, 'calendar'],
  [/QR/, 'qr'],
  [/사진|카메라/, 'camera'],
  [/복사|붙여/, 'copy'],
  [/내려받|CSV|엑셀|다운로드/, 'download'],
  [/시트|표|목록|칸/, 'list'],
  [/기록|이력/, 'history'],
  [/설정/, 'settings'],
];
const ICON_BY_KIND: Record<PatchNoteKind, IconName> = {
  new: 'sparkles',
  ui: 'layers',
  fix: 'wrench',
};

export function patchNoteIcon(item: PatchNoteItem): IconName {
  if (item.icon) return item.icon;
  let best: { at: number; icon: IconName } | null = null;
  for (const [re, icon] of ICON_BY_WORD) {
    const m = re.exec(item.text);
    if (m && (best === null || m.index < best.at)) best = { at: m.index, icon };
  }
  return best?.icon ?? ICON_BY_KIND[item.kind];
}

/** 한 날 안에서 보여 주는 차례 — 새 기능 → 화면 개선 → 고침. */
const KIND_ORDER: PatchNoteKind[] = ['new', 'ui', 'fix'];

function dayLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const wd = ['일', '월', '화', '수', '목', '금', '토'][
    new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  ];
  return `${y}년 ${m}월 ${d}일 (${wd})`;
}

export function PatchNotesButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const version = patchNotesVersion();
  const [seen, setSeen] = useState<string | null>(() => readSeenPatchNotes());
  const [open, setOpen] = useState(false);
  const updateReady = useNewVersionAvailable();
  const hasNew = updateReady || (!!version && seen !== version);

  function openNotes() {
    setOpen(true);
    markPatchNotesSeen(version);
    setSeen(version);
  }

  return (
    <>
      <button
        type="button"
        onClick={openNotes}
        className={hasNew ? 'kuun-topbar-ctl kuun-patch-new' : 'kuun-topbar-ctl'}
        aria-label={
          updateReady
            ? '패치노트. 새 버전이 나왔으니 새로고침해 주세요.'
            : hasNew
              ? '패치노트. 새 업데이트가 있습니다.'
              : '패치노트'
        }
        title={
          updateReady
            ? '새 버전이 배포됐습니다. 새로고침하면 반영됩니다.'
            : hasNew
              ? '새 업데이트가 있습니다'
              : '업데이트 내역'
        }
        data-testid="patch-notes-button"
        data-new={hasNew ? 'true' : 'false'}
        style={button}
      >
        <Icon name="scroll" size={16} aria-hidden />
        {!iconOnly && <span>패치노트</span>}
        {hasNew && <span style={dot} aria-hidden />}
      </button>
      {updateReady && (
        <button
          type="button"
          onClick={reloadToLatest}
          className="kuun-patch-new"
          style={reloadButton}
          title="새 버전이 나왔습니다. 누르면 새로고침합니다."
          data-testid="patch-notes-reload"
        >
          <Icon name="refresh" size={14} aria-hidden />
          {iconOnly ? '새로고침' : '새 버전으로 새로고침'}
        </button>
      )}
      <Drawer open={open} onClose={() => setOpen(false)} title="패치노트" width={560}>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          data-testid="patch-notes"
        >
          {updateReady && (
            <div style={reloadBanner} data-testid="patch-notes-reload-banner">
              <span>
                새 버전이 배포됐습니다. <b>새로고침</b>하면 바뀐 내용이 반영되고 최신 패치노트가
                보입니다.
              </span>
              <button type="button" onClick={reloadToLatest} style={reloadButton}>
                <Icon name="refresh" size={14} aria-hidden />
                새로고침
              </button>
            </div>
          )}
          {PATCH_NOTES.map((day, i) => (
            // 2026-10-19 오너 — "일자별로 딱 딱 나눠줘. 뒷 배경색으로 구분". 날짜마다 카드 한 장,
            // 맨 위(최신)는 브랜드 바탕, 그 아래는 두 가지 바탕을 번갈아 쓴다.
            <section key={day.date} style={dayCard(i)} data-testid={`patch-notes-day-${day.date}`}>
              <h3 style={dayHead(i)}>
                <span>{dayLabel(day.date)}</span>
                {i === 0 && <span style={latestTag}>최신</span>}
                <span style={countSt}>{day.items.length}건</span>
              </h3>
              {/* 같은 종류끼리 묶는다(새 기능 → 화면 개선 → 고침) — 오너: "섞여서 나옴". */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {KIND_ORDER.map((kind) => {
                  const items = day.items.filter((it) => it.kind === kind);
                  if (items.length === 0) return null;
                  return (
                    <div key={kind} data-testid={`patch-notes-${day.date}-${kind}`}>
                      <div style={kindHead}>
                        <span style={{ ...tag, ...KIND_STYLE[kind] }}>
                          {PATCH_NOTE_KIND_LABEL[kind]}
                        </span>
                        <span style={kindCount}>{items.length}</span>
                      </div>
                      <ul style={list}>
                        {items.map((it, j) => (
                          <li key={j} style={itemRow}>
                            <span
                              data-testid="patch-note-icon"
                              style={{
                                ...iconBox,
                                color: PART_STYLE[it.part].color,
                                background: PART_STYLE[it.part].bg,
                              }}
                            >
                              <Icon name={patchNoteIcon(it)} size={15} aria-hidden />
                            </span>
                            <span style={{ minWidth: 0 }}>
                              <span
                                data-testid="patch-note-part"
                                style={{
                                  ...partPill,
                                  color: PART_STYLE[it.part].color,
                                  background: PART_STYLE[it.part].bg,
                                }}
                              >
                                {PATCH_NOTE_PART_LABEL[it.part]}
                              </span>
                              {it.app === 'field' && <span style={appTag}>현장 앱</span>}
                              {it.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </Drawer>
    </>
  );
}

const button: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 34,
  padding: '0 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const reloadButton: CSSProperties = {
  ...button,
  marginLeft: 6,
  border: '1px solid var(--fg-brand)',
  background: 'var(--brand)',
  color: 'var(--brand-on)',
};
const reloadBanner: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 10,
  background: 'var(--bg-warning-subtle)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  lineHeight: 1.6,
};
const dot: CSSProperties = {
  position: 'absolute',
  top: -3,
  right: -3,
  width: 9,
  height: 9,
  borderRadius: '50%',
  background: 'var(--fg-danger)',
  border: '2px solid var(--bg-surface)',
};
const DAY_BG = ['var(--bg-sunken)', 'var(--bg-canvas)'];
function dayCard(i: number): CSSProperties {
  return {
    padding: '12px 16px 14px',
    borderRadius: 10,
    background: i === 0 ? 'var(--brand-subtle)' : DAY_BG[(i - 1) % 2],
    border: `1px solid ${i === 0 ? 'var(--fg-brand)' : 'var(--border-default)'}`,
  };
}
function dayHead(i: number): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '0 0 10px',
    paddingBottom: 8,
    borderBottom: `1px solid ${i === 0 ? 'var(--fg-brand)' : 'var(--border-subtle)'}`,
    fontSize: 15,
    fontWeight: 700,
  };
}
const countSt: CSSProperties = {
  marginLeft: 'auto',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--fg-muted)',
};
const latestTag: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  padding: '1px 8px',
  borderRadius: 999,
  background: 'var(--fg-danger)',
  color: 'var(--fg-inverse, #fff)',
};
const list: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
// 항목 하나 = 문단 하나. 흰 바탕 칸 + 왼쪽에 종류 색 줄로 문단끼리 또렷하게 나눈다.
// 왼쪽 색 띠(손톱 모양) 대신 종류 색의 아이콘 칸을 앞에 둔다.
const itemRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 12px',
  borderRadius: 6,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  lineHeight: 1.6,
};
const iconBox: CSSProperties = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 6,
  marginTop: 1,
};
const kindHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 6,
};
const kindCount: CSSProperties = { fontSize: 13, color: 'var(--fg-muted)', fontWeight: 600 };
// 2026-10-27 오너 "왼쪽 정렬좀 해" — 폭을 고정한 가운데 정렬이라 '고침'처럼 짧은 이름이 안으로 밀려 보였다.
// 글자만큼의 폭으로 왼쪽에 붙이고, 알약 모양 대신 작은 모서리로.
const tag: CSSProperties = {
  flexShrink: 0,
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};
/** 파트 배지 — 상태 배지(field-tones pill)와 같은 문법. 글 첫 줄 가운데에 맞춘다. */
const partPill: CSSProperties = {
  display: 'inline-block',
  marginRight: 6,
  padding: '1px 8px',
  borderRadius: 'var(--r-pill)',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.6,
  verticalAlign: 1,
  whiteSpace: 'nowrap',
};
const appTag: CSSProperties = {
  marginRight: 6,
  padding: '0 6px',
  borderRadius: 4,
  border: '1px solid var(--border-default)',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
};
